import type { CSSProperties } from "react";
import type {
  BioBackgroundStyle,
  BioButtonStyle,
  BioButtonRadius,
  BioSocialStyle,
} from "@/hooks/useBioProfile";

// The redesign prototype palette ("Paginas do Cliente - Redesign", tela Bio).
// Same values as the column defaults in 20260913100000_bio_redesign_theme.sql
// (subtle text darkened in 20260914000000_bio_subtle_text_contrast.sql so it
// clears WCAG AA on the page background — the prototype's #8A8FA2 was 2,9:1)
// and what "Restaurar padrão do redesign" puts back in the admin form.
export const BIO_REDESIGN_DEFAULTS = {
  background_style: "solid" as BioBackgroundStyle,
  background_color: "#F4F5F8",
  background_color_end: null as string | null,
  background_image_url: null as string | null,
  background_overlay_opacity: 0.4,
  button_style: "filled" as BioButtonStyle,
  button_color: "#6D3BEE",
  button_text_color: "#FFFFFF",
  button_radius: "lg" as BioButtonRadius,
  text_color: "#12131A",
  muted_text_color: "#55596A",
  subtle_text_color: "#696E82",
  card_color: "#FFFFFF",
  card_border_color: "#E6E7EF",
  social_style: "abbr" as BioSocialStyle,
  footer_text: "eliveutonsouza.com",
  show_footer: true,
};

export const BIO_CARD_SHADOW = "0 1px 2px rgba(18,20,30,.04), 0 12px 34px -18px rgba(18,20,30,.16)";

export interface BioThemeInput {
  background_style?: BioBackgroundStyle | null;
  background_color?: string | null;
  background_color_end?: string | null;
  background_image_url?: string | null;
  background_overlay_opacity?: number | null;
  button_style?: BioButtonStyle | null;
  button_color?: string | null;
  button_text_color?: string | null;
  button_radius?: BioButtonRadius | null;
  text_color?: string | null;
  muted_text_color?: string | null;
  subtle_text_color?: string | null;
  card_color?: string | null;
  card_border_color?: string | null;
  social_style?: BioSocialStyle | null;
  footer_text?: string | null;
  show_footer?: boolean | null;
}

export interface BioTheme {
  /** CSS custom properties for the page wrapper. */
  vars: Record<string, string>;
  /** Background of the page wrapper (solid/gradient/image). */
  background: CSSProperties;
  overlayOpacity: number | null;
  radiusClass: string;
  primaryStyle: CSSProperties;
  socialStyle: BioSocialStyle;
  footerText: string;
  showFooter: boolean;
}

/**
 * Everything BioView needs from a profile, with every missing/null field
 * falling back to the prototype value — so a half-filled admin form and a
 * DB row from before the redesign both render correctly.
 */
export function resolveBioTheme(p: BioThemeInput | null | undefined): BioTheme {
  const d = BIO_REDESIGN_DEFAULTS;
  const t = {
    background_style: p?.background_style ?? d.background_style,
    background_color: p?.background_color || d.background_color,
    background_color_end: p?.background_color_end ?? null,
    background_image_url: p?.background_image_url ?? null,
    background_overlay_opacity: p?.background_overlay_opacity ?? d.background_overlay_opacity,
    button_style: p?.button_style ?? d.button_style,
    button_color: p?.button_color || d.button_color,
    button_text_color: p?.button_text_color || d.button_text_color,
    button_radius: p?.button_radius ?? d.button_radius,
  };
  const ink = p?.text_color || d.text_color;
  const ink2 = p?.muted_text_color || d.muted_text_color;
  const ink3 = p?.subtle_text_color || d.subtle_text_color;
  const surface = p?.card_color || d.card_color;
  const line = p?.card_border_color || d.card_border_color;

  return {
    vars: {
      "--bio-ink": ink,
      "--bio-ink2": ink2,
      "--bio-ink3": ink3,
      "--bio-surface": surface,
      "--bio-line": line,
      "--bio-accent": t.button_color,
      "--bio-accent-fg": t.button_text_color,
      "--bio-shadow": BIO_CARD_SHADOW,
    },
    background: getBioBackgroundStyle(t),
    overlayOpacity: t.background_style === "image" ? t.background_overlay_opacity : null,
    radiusClass: BIO_BUTTON_RADIUS_CLASS[t.button_radius] ?? BIO_BUTTON_RADIUS_CLASS.lg,
    primaryStyle: getBioButtonStyle(t),
    socialStyle: p?.social_style ?? d.social_style,
    footerText: p?.footer_text ?? d.footer_text,
    showFooter: p?.show_footer ?? d.show_footer,
  };
}

interface BackgroundInput {
  background_style: BioBackgroundStyle;
  background_color: string;
  background_color_end?: string | null;
  background_image_url?: string | null;
}

export function getBioBackgroundStyle(profile: BackgroundInput): CSSProperties {
  if (profile.background_style === "gradient") {
    return {
      background: `linear-gradient(180deg, ${profile.background_color}, ${profile.background_color_end || profile.background_color})`,
    };
  }
  if (profile.background_style === "image" && profile.background_image_url) {
    return {
      backgroundImage: `url(${profile.background_image_url})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundColor: profile.background_color,
    };
  }
  return { backgroundColor: profile.background_color };
}

// Combinam com a identidade visual do site (src/index.css): roxo da marca
// (--primary/--primary-glow), o tom de lavanda do --accent claro, e o
// --background escuro do tema dark.
export interface BioGradientPreset {
  id: string;
  label: string;
  from: string;
  to: string;
}

export const BIO_GRADIENT_PRESETS: BioGradientPreset[] = [
  { id: "redesign", label: "Redesign claro", from: "#F4F5F8", to: "#F4F5F8" },
  { id: "white", label: "Branco", from: "#FFFFFF", to: "#F5F0FF" },
  { id: "brand", label: "Roxo da Marca", from: "#7C3AED", to: "#9B6EF3" },
  { id: "dark", label: "Escuro", from: "#14161A", to: "#1E212B" },
  { id: "dark-brand", label: "Escuro Roxo", from: "#14161A", to: "#3B2170" },
];

export const BIO_BUTTON_RADIUS_CLASS: Record<BioButtonRadius, string> = {
  none: "rounded-none",
  md: "rounded-md",
  lg: "rounded-[14px]",
  full: "rounded-full",
};

interface ButtonInput {
  button_style: BioButtonStyle;
  button_color: string;
  button_text_color: string;
}

export function getBioButtonStyle(profile: ButtonInput): CSSProperties {
  if (profile.button_style === "outline") {
    return {
      backgroundColor: "transparent",
      border: `1px solid ${profile.button_color}`,
      color: profile.button_color,
    };
  }
  return {
    backgroundColor: profile.button_color,
    color: profile.button_text_color,
    opacity: profile.button_style === "soft" ? 0.85 : 1,
  };
}

// ─── Link helpers shared by BioView and tests ───────────────────────────────

interface PrimaryCandidate {
  id: string;
  kind: string;
  is_primary?: boolean | null;
}

/** The flagged link, else the first regular link — there's always exactly one highlight. */
export function pickPrimaryLinkId(regularLinks: PrimaryCandidate[]): string | null {
  return (
    regularLinks.find((l) => l.is_primary && l.kind === "link")?.id ??
    regularLinks.find((l) => l.kind === "link")?.id ??
    null
  );
}

const SOCIAL_ABBR: Record<string, string> = {
  linkedin: "in",
  instagram: "ig",
  github: "gh",
  youtube: "yt",
  whatsapp: "wa",
  x: "x",
  tiktok: "tt",
  facebook: "fb",
  telegram: "tg",
  email: "@",
  phone: "tel",
  website: "www",
};

export function socialAbbr(link: {
  icon?: string | null;
  url?: string | null;
  title?: string | null;
}): string {
  if (link.icon && SOCIAL_ABBR[link.icon]) return SOCIAL_ABBR[link.icon];
  const host = (() => {
    try {
      return new URL(link.url || "").hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  })();
  for (const key of Object.keys(SOCIAL_ABBR)) {
    if (host.includes(key)) return SOCIAL_ABBR[key];
  }
  return (link.title || host || "•").slice(0, 2).toLowerCase();
}
